/**
 * Yoshi: ingredients slideshow (sections/plus-ingredients-slideshow.liquid) - <plus-ingredients-slideshow>.
 *
 * - Slider: a native scroll-snap track ([data-plus-ings-track]); arrows ([data-plus-ings-prev|next]) and dots
 *   ([data-plus-ings-dots], built here) scroll it one card at a time. data-type="loop" wraps from the last
 *   position to the first (and back); "slide" stops at the ends. Cards per view, gaps and peek come from CSS.
 * - Autoplay (data-autoplay, data-autoplay-speed in seconds): pauses on hover/focus, while the tab is hidden or
 *   the section is off screen, and never runs for prefers-reduced-motion.
 * - 3D fan (data-fan="true"): writes --plus-ings-offset (signed, in cards) and --plus-ings-distance (absolute)
 *   on each slide while scrolling; the CSS turns them into rotateY/translateZ (reduced-motion users get none).
 * - Flip cards: [data-plus-ings-flip] toggles .is-flipped on the card; the hidden face is made `inert`.
 * - Popup: a native <dialog> ([data-plus-ings-popup]) opened by [data-plus-ings-open]; closes on Esc, the close
 *   button or a click on the backdrop.
 * - Theme editor: selecting a block scrolls to its card (shopify:block:select).
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class PlusIngredientsSlideshow extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {number | undefined} */
  #autoplayTimer;

  #frame = 0;
  #paused = false;
  #visible = true;
  #positions = 1;
  #current = 0;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const track = this.#track;
    if (!track) return;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    track.addEventListener('scroll', this.#onScroll, { passive: true, signal });

    this.addEventListener('pointerenter', () => this.#setPaused(true), { signal });
    this.addEventListener('pointerleave', () => this.#setPaused(this.contains(document.activeElement)), { signal });
    this.addEventListener('focusin', () => this.#setPaused(true), { signal });
    this.addEventListener(
      'focusout',
      (event) => {
        if (!(event.relatedTarget instanceof Node) || !this.contains(event.relatedTarget)) this.#setPaused(false);
      },
      { signal }
    );
    document.addEventListener('visibilitychange', () => this.#restartAutoplay(), { signal });
    reducedMotion.addEventListener('change', () => this.#restartAutoplay(), { signal });

    const popup = this.#popup;
    popup?.addEventListener(
      'click',
      (event) => {
        if (event.target === popup) popup.close();
      },
      { signal }
    );

    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    document.addEventListener(
      'shopify:block:deselect',
      (event) => {
        if (event.target instanceof Node && this.contains(event.target)) this.#setPaused(false);
      },
      { signal }
    );

    this.#resizeObserver = new ResizeObserver(() => this.#layout());
    this.#resizeObserver.observe(track);

    this.#intersectionObserver = new IntersectionObserver((entries) => {
      this.#visible = entries.some((entry) => entry.isIntersecting);
      this.#restartAutoplay();
    });
    this.#intersectionObserver.observe(this);

    this.#layout();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#resizeObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
    window.clearInterval(this.#autoplayTimer);
    cancelAnimationFrame(this.#frame);
  }

  /** @returns {HTMLElement | null} */
  get #track() {
    return this.querySelector('[data-plus-ings-track]');
  }

  /** @returns {HTMLElement[]} */
  get #slides() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-plus-ings-slide]')));
  }

  /** @returns {HTMLDialogElement | null} */
  get #popup() {
    const popup = this.querySelector('[data-plus-ings-popup]');
    return popup instanceof HTMLDialogElement ? popup : null;
  }

  get #loops() {
    return this.dataset.type === 'loop';
  }

  /** Distance in px between the starts of two neighbouring slides. */
  get #step() {
    const [first, second] = this.#slides;
    if (!first) return 0;
    if (second) return Math.abs(second.offsetLeft - first.offsetLeft);
    return first.offsetWidth;
  }

  /** Cards visible at once, read back from the rendered sizes. */
  get #perView() {
    const track = this.#track;
    const step = this.#step;
    if (!track || !step) return 1;
    const styles = getComputedStyle(track);
    const inner = track.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
    const gap = parseFloat(styles.columnGap) || 0;
    return Math.max(1, Math.round((inner + gap) / step));
  }

  #layout() {
    const count = this.#slides.length;
    this.#positions = Math.max(1, count - this.#perView + 1);
    this.#buildDots();
    this.#sync();
    this.#restartAutoplay();
  }

  #buildDots() {
    const dots = this.querySelector('[data-plus-ings-dots]');
    if (!(dots instanceof HTMLElement)) return;
    if (dots.childElementCount === this.#positions) return;

    dots.replaceChildren();
    if (this.#positions < 2) return;

    for (let index = 0; index < this.#positions; index += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'plus-ings__dot';
      dot.dataset.plusIngsDot = String(index);
      dot.setAttribute('aria-label', `${index + 1} / ${this.#positions}`);
      dots.append(dot);
    }
  }

  /** @param {number} index */
  #goTo(index) {
    const track = this.#track;
    if (!track) return;

    let target = index;
    if (target > this.#positions - 1) target = this.#loops ? 0 : this.#positions - 1;
    if (target < 0) target = this.#loops ? this.#positions - 1 : 0;

    const slide = this.#slides[target];
    const first = this.#slides[0];
    if (!slide || !first) return;

    track.scrollTo({
      left: slide.offsetLeft - first.offsetLeft,
      behavior: reducedMotion.matches ? 'auto' : 'smooth',
    });
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#sync());
  };

  /** Updates the current index, dots, arrows and the fan offsets from the scroll position. */
  #sync() {
    const track = this.#track;
    const step = this.#step;
    if (!track || !step) return;

    this.#current = Math.min(this.#positions - 1, Math.max(0, Math.round(Math.abs(track.scrollLeft) / step)));

    for (const dot of this.querySelectorAll('[data-plus-ings-dot]')) {
      dot.setAttribute('aria-current', String(Number(/** @type {HTMLElement} */ (dot).dataset.plusIngsDot) === this.#current));
    }

    const atStart = Math.abs(track.scrollLeft) < 2;
    const atEnd = Math.abs(track.scrollLeft) >= track.scrollWidth - track.clientWidth - 2;
    for (const button of this.querySelectorAll('[data-plus-ings-prev]')) {
      /** @type {HTMLButtonElement} */ (button).disabled = this.#positions < 2 || (!this.#loops && atStart);
    }
    for (const button of this.querySelectorAll('[data-plus-ings-next]')) {
      /** @type {HTMLButtonElement} */ (button).disabled = this.#positions < 2 || (!this.#loops && atEnd);
    }

    if (this.dataset.fan !== 'true') return;
    // Measure from the layout (offsetLeft), not the transformed boxes, so the fan doesn't feed back into itself.
    const first = this.#slides[0];
    const scrolled = Math.abs(track.scrollLeft);
    for (const slide of this.#slides) {
      const start = Math.abs(slide.offsetLeft - (first?.offsetLeft ?? 0));
      const offset = Math.max(-2, Math.min(2, (start - scrolled) / step));
      // Cards inside the "per view" window stay flat; only the ones beyond it tilt away.
      const beyond = offset > 0 ? Math.max(0, offset - (this.#perView - 1)) : offset;
      slide.style.setProperty('--plus-ings-offset', beyond.toFixed(3));
      slide.style.setProperty('--plus-ings-distance', Math.abs(beyond).toFixed(3));
      // The current card stays on top of the tilted neighbours that tuck in behind it.
      slide.style.zIndex = String(10 - Math.round(Math.abs(beyond)));
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-plus-ings-prev]')) {
      this.#goTo(this.#current - 1);
    } else if (target.closest('[data-plus-ings-next]')) {
      this.#goTo(this.#current + 1);
    } else if (target.closest('[data-plus-ings-dot]')) {
      const dot = /** @type {HTMLElement} */ (target.closest('[data-plus-ings-dot]'));
      this.#goTo(Number(dot.dataset.plusIngsDot) || 0);
    } else if (target.closest('[data-plus-ings-flip]')) {
      const card = target.closest('[data-plus-ings-card]');
      if (card instanceof HTMLElement) this.#flip(card);
    } else if (target.closest('[data-plus-ings-open]')) {
      this.#popup?.showModal();
    } else if (target.closest('[data-plus-ings-close]')) {
      this.#popup?.close();
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-plus-ings-track]')) return;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#goTo(this.#current + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#goTo(this.#current - 1);
    }
  };

  /** @param {HTMLElement} card */
  #flip(card) {
    const flipped = card.classList.toggle('is-flipped');
    const front = card.querySelector('.plus-ings__face--front');
    const back = card.querySelector('[data-plus-ings-back]');
    if (front instanceof HTMLElement) front.inert = flipped;
    if (back instanceof HTMLElement) back.inert = !flipped;

    const nextButton = (flipped ? back : front)?.querySelector('[data-plus-ings-flip]');
    if (nextButton instanceof HTMLElement) nextButton.focus({ preventScroll: true });
  }

  /** @param {boolean} paused */
  #setPaused(paused) {
    this.#paused = paused;
    this.#restartAutoplay();
  }

  #restartAutoplay() {
    window.clearInterval(this.#autoplayTimer);
    this.#autoplayTimer = undefined;

    const seconds = Number(this.dataset.autoplaySpeed) || 5;
    const enabled =
      this.dataset.autoplay === 'true' &&
      !reducedMotion.matches &&
      !this.#paused &&
      this.#visible &&
      document.visibilityState === 'visible' &&
      this.#positions > 1;
    if (!enabled) return;

    this.#autoplayTimer = window.setInterval(() => {
      // Autoplay always wraps, even when manual navigation stops at the ends.
      this.#goTo(this.#current >= this.#positions - 1 ? 0 : this.#current + 1);
    }, Math.max(2, seconds) * 1000);
  }

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    const slide = event.target instanceof Element ? event.target.closest('[data-plus-ings-slide]') : null;
    if (!(slide instanceof HTMLElement) || !this.contains(slide)) return;
    // Keep autoplay from scrolling the selected card away while it is being edited.
    this.#setPaused(true);
    const index = this.#slides.indexOf(slide);
    if (index >= 0) this.#goTo(Math.min(index, this.#positions - 1));
  };
}

if (!customElements.get('plus-ingredients-slideshow')) {
  customElements.define('plus-ingredients-slideshow', PlusIngredientsSlideshow);
}
